/**
 * Normalizes the inconsistent winner strings from different games
 * into a standard { winnerId, isDraw } format.
 *
 * Game winner formats:
 * - TicTacToe: 'X' (host) or 'O' (guest)
 * - Battleships/Snake: 'you' or 'opponent' (relative to caller)
 * - Checkers: 'red' (host) or 'black' (guest)
 * - WordDuel: 'host' or 'guest'
 * - SpaceShooter: null (no competitive winner)
 * - CoopPong: 'Score: N' (no competitive winner)
 */

interface NormalizedResult {
  winnerRole: 'host' | 'guest' | null;
  isDraw: boolean;
  shouldRecord: boolean; // false for non-competitive games
}

export function normalizeWinner(
  winner: string | null,
  isHost: boolean,
  gameType: string
): NormalizedResult {
  // Non-competitive games — don't record
  if (gameType === 'spaceshooter' || gameType === 'cooppong') {
    return { winnerRole: null, isDraw: false, shouldRecord: false };
  }

  // Draw
  if (winner === null || winner === 'draw') {
    return { winnerRole: null, isDraw: true, shouldRecord: true };
  }

  const w = winner.toLowerCase();

  // Direct role strings (WordDuel)
  if (w === 'host') return { winnerRole: 'host', isDraw: false, shouldRecord: true };
  if (w === 'guest') return { winnerRole: 'guest', isDraw: false, shouldRecord: true };

  // TicTacToe: X = host, O = guest
  if (w === 'x') return { winnerRole: 'host', isDraw: false, shouldRecord: true };
  if (w === 'o') return { winnerRole: 'guest', isDraw: false, shouldRecord: true };

  // Checkers: red = host, black = guest
  if (w === 'red') return { winnerRole: 'host', isDraw: false, shouldRecord: true };
  if (w === 'black') return { winnerRole: 'guest', isDraw: false, shouldRecord: true };

  // Relative strings (Battleships, Snake)
  if (w === 'you') return { winnerRole: isHost ? 'host' : 'guest', isDraw: false, shouldRecord: true };
  if (w === 'opponent') return { winnerRole: isHost ? 'guest' : 'host', isDraw: false, shouldRecord: true };

  // Fallback — don't record unknown formats
  return { winnerRole: null, isDraw: false, shouldRecord: false };
}
